import { Button } from '@/components/ui/button';
import { useCart } from '@/hooks/use-cart';
import { fetchShopOptionsCounting } from '@/lib/api/fetch-shop-options-counting';
import { checkCartStock } from '@/lib/check-cart-stock';
import { useAppDispatch } from '@/lib/client-hook/use-app-dispatch';
import { useDictionary } from '@/lib/context/locale-provider';
import { Languages } from '@/lib/dictionaries';
import { Cart } from '@/schema/cart';
import { useDeleteCartItemOptionsMutation } from '@/slice/cartApi';
import {
  setCartCurrency,
  setCartRedirectUrl,
  setCarts,
  setCartSerials,
} from '@/slice/cartSlice';
import { setErrorMessage } from '@/slice/error';
import { useRouter } from 'next/navigation';
import React from 'react';

function CartTotalBox({ lang, carts }: { lang: Languages; carts: Cart[] }) {
  const dict = useDictionary();
  const router = useRouter();
  const dispatch = useAppDispatch();
  const { state } = useCart();

  const [deleteCartItemOptions] = useDeleteCartItemOptionsMutation();

  const { totalQty, totalPrice } = React.useMemo(() => {
    return carts.reduce(
      (acc, { cart }) => {
        const result = cart.result.reduce(
          (bcc, { count, serial, userPrice, isSoldOut }) => {
            if (isSoldOut) {
              bcc.totalQty += 0;
              bcc.totalPrice += 0;
            }

            if (state.selectedItems.includes(serial)) {
              bcc.totalQty += count;
              bcc.totalPrice += userPrice * count;
            }

            return bcc;
          },
          { totalQty: 0, totalPrice: 0 },
        );
        acc.totalQty += Number(result.totalQty.toFixed(1));
        acc.totalPrice += Number(result.totalPrice.toFixed(2));
        return acc;
      },
      { totalQty: 0, totalPrice: 0 },
    );
  }, [carts, state]);

  const handleClick = async () => {
    if (state.selectedItems.length > 0) {
      const selectedCarts = filterCartBySelectedItems(
        carts,
        state.selectedItems,
      );
      const { hasSoldOut, stockMap } = await checkCartStock({
        carts: selectedCarts,
        fetchStock: fetchShopOptionsCounting,
        onSoldOut: ({ shopId, serial }) => {
          deleteCartItemOptions([serial]);
        },
      });

      if (hasSoldOut) {
        dispatch(setErrorMessage(dict!.shop.error.soldOutRefresh));
      } else {
        localStorage.setItem('cart-redirect-url', `/${lang}/cart`);
        dispatch(setCartSerials(state.selectedItems));
        dispatch(setCarts(selectedCarts));
        dispatch(setCartRedirectUrl(`/${lang}/cart`));
        dispatch(setCartCurrency(dict!.common.currency.replace('원', 'KRW')));
        router.replace(`/${lang}/pay`);
      }
    }
  };

  return (
    <div className="lg:pt-[60px] flex flex-col gap-[3px]">
      <div className="w-full text-center giftifan3 border border-solid border-[#C177E4] font-bold text-lg rounded-[8px] order-2 mt-[7px] lg:mt-0">
        {dict!.cart.couponApplyNextPage}
      </div>
      <div className="shadow-md flex flex-col gap-1 p-5 rounded-xl order-1 lg:order-2">
        <h2 className="font-bold text-2xl giftifan5 pb-1">
          {dict!.cart.purchaseAmount}
        </h2>
        <p className="font-bold text-lg giftifan5">
          {dict!.cart.totalPurchaseQty}{' '}
          <span className="float-end">
            {dict!.common.count.replace('{n}', totalQty)}
          </span>
        </p>
        <p className="font-bold text-lg giftifan5">
          {dict!.cart.totalPurchaseAmount}{' '}
          <span className="float-end">
            {totalPrice.toLocaleString('en-US')}
            {dict!.common.currency}
          </span>
        </p>
        <div className="basic-shadow bg-white mt-4 p-5 fixed bottom-0 left-0 w-full lg:relative lg:p-0">
          <Button
            className="bg-gradient-giftifan font-bold text-lg w-full"
            onClick={handleClick}
          >
            {dict!.cart.payItems
              .replace('{price}', totalPrice.toLocaleString('en-US'))
              .replace('{n}', totalQty)}
          </Button>
        </div>
      </div>
    </div>
  );
}

function filterCartBySelectedItems(carts: Cart[], selectedItems: string[]) {
  const selectedSet = new Set(selectedItems);

  return carts
    .map(cart => {
      const filteredResults = cart.cart.result.filter(item =>
        selectedSet.has(item.serial),
      );

      if (filteredResults.length === 0) return null;

      let totalWeight = 0;
      let totalPrice = 0;
      let totalSalesCounting = 0;
      let totalQuantity = 0;

      for (const item of filteredResults) {
        totalWeight += Number((item.weight * item.count).toFixed(2));
        totalPrice += item.userPrice * item.count;
        totalSalesCounting += item.salesCounting;
        totalQuantity += item.count;
      }

      return {
        ...cart,
        cart: {
          ...cart.cart,
          totalQuantity,
          totalWeight,
          totalPrice,
          totalSalesCounting,
          result: filteredResults,
        },
      };
    })
    .filter(cart => cart !== null);
}

export default CartTotalBox;
